import { Link } from 'react-router-dom';
import {
  CommandLineIcon,
  KeyIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  BookOpenIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import MarketingLayout from '../components/marketing/MarketingLayout';

const toolCategories = [
  {
    tier: 'Professional',
    description: 'Available to all paid tiers',
    tools: [
      { name: 'hmda_validate_lar', description: 'Validate a HMDA LAR file against 160+ CFPB edit checks' },
      { name: 'hmda_get_validation', description: 'Retrieve a validation summary by ID' },
      { name: 'hmda_list_validations', description: 'List recent validations for the team' },
      { name: 'hmda_get_edits', description: 'Get edit results with filtering by type, severity, and code' },
      { name: 'check_marketing_compliance', description: 'Check marketing content against federal regulations' },
      { name: 'get_compliance_report', description: 'Retrieve a compliance report by ID' },
      { name: 'list_compliance_reports', description: 'List recent compliance reports' },
      { name: 'compliance_health_score', description: 'Get overall compliance health score and breakdown' },
      { name: 'compliance_summary', description: 'Aggregated compliance activity with trends and narrative' },
      { name: 'policy_gap_analysis', description: 'Identify gaps in your compliance policy coverage' },
      { name: 'search_regulations', description: 'Search the regulatory knowledge base' },
      { name: 'get_regulation_detail', description: 'Get detailed regulation text and guidance' },
      { name: 'get_product_compliance', description: 'Check compliance requirements for a product' },
      { name: 'regulatory_changes_list', description: 'List recent regulatory changes and updates' },
      { name: 'exam_readiness', description: 'Get exam readiness score and checklist' },
      { name: 'get_campaign_compliance', description: 'Get compliance status for a marketing campaign' },
      { name: 'check_social_media_post', description: 'Check a social media post for compliance violations with enforcement context' },
    ],
  },
  {
    tier: 'Enterprise',
    description: 'Additional tools for Enterprise customers',
    tools: [
      { name: 'fair_lending_risk_assessment', description: 'Retrieve fair lending risk assessment results' },
      { name: 'fair_lending_screen', description: 'Screen text for discriminatory language and fair lending red flags' },
      { name: 'regression_analysis', description: 'IFLEP-aligned regression analysis (denial, pricing, steering)' },
      { name: 'exam_list', description: 'List regulatory exams with status and findings' },
      { name: 'enforcement_actions_list', description: 'Search recent enforcement actions by agency' },
      { name: 'lar_builder_status', description: 'List LAR Builder workspaces and completion status' },
      { name: 'lar_readiness', description: 'Filing readiness dashboard with deadline countdown' },
      { name: 'lo_gap_report', description: 'Per-loan-officer HMDA data gap report' },
      { name: 'hmda_reportability_check', description: 'Determine if a loan is HMDA-reportable' },
      { name: 'hmda_field_checklist', description: 'Dynamic HMDA field checklist by loan characteristics' },
      { name: 'cra_lending_analysis', description: 'CRA lending analysis with geographic and borrower income distribution' },
      { name: 'cra_gap_analysis', description: 'AI-powered CRA gap analysis with cross-module correlations' },
      { name: 'cra_cd_activities_summary', description: 'Summary of CRA community development activities by type and purpose' },
      { name: 'cra_assessment_areas', description: 'List CRA assessment areas with demographic data' },
      { name: 'cra_exam_readiness', description: 'CRA exam readiness score and checklist with AI narrative' },
    ],
  },
];

const usageExamples = [
  {
    title: 'Check marketing content',
    prompt: 'Check this ad for compliance issues: "Lowest rates guaranteed! No credit check needed. Apply now for our exclusive home loan."',
    tool: 'check_marketing_compliance',
  },
  {
    title: 'Validate HMDA LAR file',
    prompt: 'Validate my HMDA LAR file for the 2025 filing year. [Paste pipe-delimited LAR content]',
    tool: 'hmda_validate_lar',
  },
  {
    title: 'Check loan reportability',
    prompt: 'Is a conventional home purchase loan that is dwelling-secured and the property is in our MSA HMDA-reportable?',
    tool: 'hmda_reportability_check',
  },
  {
    title: 'Get compliance health score',
    prompt: "What's our overall compliance health score right now?",
    tool: 'compliance_health_score',
  },
  {
    title: 'Search regulations',
    prompt: 'What are the Reg DD requirements for savings account disclosures?',
    tool: 'search_regulations',
  },
  {
    title: 'Check social media post',
    prompt: 'Check this social post for compliance: "Lowest rates guaranteed! Apply now for our exclusive home loan — no credit check needed!"',
    tool: 'check_social_media_post',
  },
  {
    title: 'CRA exam readiness',
    prompt: "How ready are we for our CRA exam? What's our readiness score?",
    tool: 'cra_exam_readiness',
  },
];

export default function McpDocsPage() {
  return (
    <MarketingLayout>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <CpuChipIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">MCP Integration</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl">
            Connect Clarid to any AI assistant that supports the Model Context Protocol (MCP).
            Use compliance intelligence directly from Claude, ChatGPT, Microsoft Copilot, or any MCP-compatible tool.
          </p>
        </div>

        {/* Quick Start */}
        <section className="mb-12 bg-white rounded-xl border p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CommandLineIcon className="h-5 w-5" />
            Quick Start
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-1">1. Get your API key</h3>
              <p className="text-sm text-gray-600">
                Go to <Link to="/settings" className="text-blue-600 hover:underline">Settings &rarr; Integrations</Link> and
                generate an API key. Or connect via OAuth for interactive use.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-1">2. Configure your MCP client</h3>
              <div className="bg-gray-900 rounded-lg p-4 text-sm font-mono text-gray-100 overflow-x-auto">
                <pre>{`{
  "mcpServers": {
    "clarid": {
      "url": "https://api.clarid.ai/mcp",
      "transport": "streamable-http",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`}</pre>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-1">3. Start using compliance tools</h3>
              <p className="text-sm text-gray-600">
                Your AI assistant will discover Clarid's tools automatically. Ask it to check content,
                validate a LAR file, or get your compliance health score.
              </p>
            </div>
          </div>
        </section>

        {/* Authentication */}
        <section className="mb-12 bg-white rounded-xl border p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <KeyIcon className="h-5 w-5" />
            Authentication
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">API Key</h3>
              <p className="text-sm text-gray-600 mb-2">
                For server-to-server integrations and CLI tools. Pass your API key in the Authorization header.
              </p>
              <code className="text-xs bg-gray-100 rounded px-2 py-1 text-gray-700">
                Authorization: Bearer sk_clarid_...
              </code>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">OAuth 2.0</h3>
              <p className="text-sm text-gray-600 mb-2">
                For interactive use from AI assistants. Supports Dynamic Client Registration (RFC 7591) and PKCE (RFC 7636).
              </p>
              <div className="text-xs text-gray-500 space-y-1">
                <div>Discovery: <code className="bg-gray-100 rounded px-1">/.well-known/oauth-authorization-server</code></div>
                <div>Authorize: <code className="bg-gray-100 rounded px-1">/oauth/authorize</code></div>
                <div>Token: <code className="bg-gray-100 rounded px-1">/oauth/token</code></div>
                <div>Register: <code className="bg-gray-100 rounded px-1">/oauth/register</code></div>
              </div>
            </div>
          </div>
        </section>

        {/* Tools */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5" />
            Available Tools ({toolCategories.reduce((sum, c) => sum + c.tools.length, 0)})
          </h2>

          {toolCategories.map((category) => (
            <div key={category.tier} className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-1">{category.tier}</h3>
              <p className="text-sm text-gray-500 mb-3">{category.description}</p>
              <div className="bg-white rounded-xl border divide-y">
                {category.tools.map((tool) => (
                  <div key={tool.name} className="px-4 py-3 flex items-start gap-3">
                    <code className="text-xs bg-blue-50 text-blue-700 rounded px-2 py-0.5 font-mono whitespace-nowrap mt-0.5">
                      {tool.name}
                    </code>
                    <span className="text-sm text-gray-600">{tool.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Usage Examples */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <BookOpenIcon className="h-5 w-5" />
            Usage Examples
          </h2>
          <div className="space-y-4">
            {usageExamples.map((example) => (
              <div key={example.title} className="bg-white rounded-xl border p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{example.title}</h3>
                  <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5 font-mono">
                    {example.tool}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700 italic">
                  &ldquo;{example.prompt}&rdquo;
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Links */}
        <section className="bg-blue-50 rounded-xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Resources</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              MCP Specification
            </a>
            <Link to="/support" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Help Center
            </Link>
            <Link to="/pricing" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Pricing & Tiers
            </Link>
          </div>
        </section>
      </div>

    </MarketingLayout>
  );
}
